import { serverConfigurationsClient } from '@/helpers/environmentHelper';
import { useAppDispatch, useAppSelector } from '@/state/hooks';
import { selectSystemStatus, setSystemStatus } from '@/state/reducers/generalSlice';
import useInterval from '@/utils/hooks/useInterval';
import useLanguage from '@/utils/hooks/useLanguage';

import { flip, offset, shift, useFloating } from '@floating-ui/react';
import { useMemo, useState } from 'react';

export type SystemStatus = 'healthy' | 'degraded' | 'down';

interface StatusIndicatorProps {
    showLabel?: boolean;
    size?: 'sm' | 'md' | 'lg';
}

function isInWeeklyMaintenance() {
    const now = new Date();
    const nyTime = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));

    const day = nyTime.getDay();
    const hours = nyTime.getHours();
    const minutes = nyTime.getMinutes();

    const startMinutes = 7 * 60; // 07:00
    const endMinutes = 8 * 60; // 08:00
    const currentMinutes = hours * 60 + minutes;

    return day === 6 && currentMinutes >= startMinutes && currentMinutes <= endMinutes;
}

const sizeConfig = {
    sm: 'w-2 h-2',
    md: 'w-3 h-3',
    lg: 'w-4 h-4'
};

function StatusIndicator({ showLabel = true, size = 'md' }: StatusIndicatorProps) {
    const lang = useLanguage();

    const dispatch = useAppDispatch();
    const systemStatus = useAppSelector(selectSystemStatus);

    const [showTooltip, setShowTooltip] = useState(false);

    const statusConfig = useMemo(() => {
        return {
            healthy: {
                color: 'bg-brand-green-light',
                glow: 'shadow-brand-green-light/50',
                text: lang.login.systemStatus.operational,
                textColor: 'text-brand-green'
            },
            degraded: {
                color: 'bg-yellow-500',
                glow: 'shadow-yellow-500/50',
                text: lang.login.systemStatus.attempting,
                textColor: 'text-yellow-700'
            },
            down: {
                color: 'bg-brand-red-light',
                glow: 'shadow-brand-red-light/50',
                text: lang.login.systemStatus.outage,
                textColor: 'text-brand-red'
            }
        };
    }, [lang]);

    const config = useMemo(() => {
        if (isInWeeklyMaintenance() && ['degraded', 'down'].includes(systemStatus)) {
            return {
                color: 'bg-yellow-500',
                glow: 'shadow-yellow-500/50',
                text: lang.login.systemStatus.maintenance,
                textColor: 'text-yellow-700'
            };
        }
        return statusConfig[systemStatus];
    }, [systemStatus, statusConfig, lang]);

    const sizeClass = useMemo(() => sizeConfig[size], [size]);

    useInterval(
        async () => {
            try {
                await serverConfigurationsClient.unary.getConfigurations({});
                dispatch(setSystemStatus('healthy'));
            } catch (error) {
                dispatch(setSystemStatus('down'));
            }
        },
        15000,
        true
    );

    // -----------------------------
    // 🧩 Floating UI
    // -----------------------------
    const { refs, floatingStyles, context } = useFloating({
        placement: 'top',
        middleware: [
            offset(8), // spacing
            flip(), // auto flip if obstructed
            shift() // shift into viewport if clipped
        ]
    });

    return (
        <div className="flex items-center gap-3">
            <div
                ref={refs.setReference}
                className="relative"
                onMouseEnter={() => setShowTooltip(true)}
                onMouseLeave={() => setShowTooltip(false)}>
                <div
                    className={`${sizeClass} rounded-full ${config.color} ${config.glow} 
                    shadow-lg animate-pulse cursor-pointer`}
                />
                <div className={`absolute inset-0 ${sizeClass} rounded-full ${config.color} opacity-40 blur-sm`} />

                {/* Tooltip */}
                {!showLabel && showTooltip && (
                    <div
                        ref={refs.setFloating}
                        style={floatingStyles}
                        className="px-3 py-1.5 bg-brand-neutral-700 text-brand-text text-xs 
                        rounded-lg whitespace-nowrap z-50 shadow-lg">
                        {config.text}
                    </div>
                )}
            </div>

            {showLabel && <span className={`text-sm font-medium ${config.textColor}`}>{config.text}</span>}
        </div>
    );
}

export default StatusIndicator;
